/**
 * RangCarte — une entrée du classement hors podium (rang 4 et plus) : le rang
 * en gris, la tuile, le nom et ses plateformes, le montant à droite. Seule,
 * c'est une carte en verre ; dans une `RangListe`, une ligne séparée de la
 * suivante par une hairline. L'entrée « Toi » porte un liseré néon.
 *
 * `dense` : la ligne de 49 px pour une liste qui doit tenir dans l'écran et se
 * paginer (le « reste de l'équipe » du classement des chatteurs, le « reste »
 * des affiliés) ; la pagination reste chez l'appelant, qui compte avec
 * `HAUTEUR_LIGNE`.
 */
import { motion, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";

import { cn } from "../utils";
import { CreditCard, TelegramLogo, XLogo } from "../icons";

import "./classement.css";
import { formatAvec } from "./format";
import { Tuile } from "./Tuile";
import { cleDe, type EntreeClassement, type OptionsFormat, type Plateforme } from "./types";

const PLATEFORMES: Record<Plateforme, { icone: ReactNode; titre: string }> = {
  telegram: { icone: <TelegramLogo size={11} weight="fill" />, titre: "Telegram" },
  x: { icone: <XLogo size={11} weight="fill" />, titre: "X" },
  dropp: { icone: <CreditCard size={11} weight="fill" />, titre: "Dropp" },
};

/** Hauteur d'une ligne (px, hairline comprise) — pour compter ce qui tient. */
export const HAUTEUR_LIGNE = { normale: 73, dense: 49 } as const;

export interface RangCarteProps extends OptionsFormat {
  entree: EntreeClassement;
  onClick?: (entree: EntreeClassement) => void;
  /** `carte` (défaut) : seule, avec son bord. `ligne` : dans une liste. */
  variante?: "carte" | "ligne";
  /** Ligne serrée (49 px) pour une liste paginée. */
  dense?: boolean;
  className?: string;
}

export function RangCarte({
  entree,
  format = "montant",
  devise = "EUR",
  suffixe,
  formater,
  onClick,
  variante = "carte",
  dense = false,
  className,
}: RangCarteProps) {
  const cliquable = Boolean(onClick);
  const cache = entree.montant === null;
  const classes = cn(
    "cl-root cl-rang flex w-full items-center text-left",
    dense ? "gap-2.5 px-3 py-2" : "gap-3 px-4 py-3",
    variante === "carte" && "cl-carte",
    className,
  );
  const contenu = (
    <>
      <span
        className={cn(
          "cl-etiquette shrink-0 text-center text-faint",
          dense ? "w-5 text-[11px]" : "w-6 text-[12px]",
        )}
      >
        {entree.rang}
      </span>
      <Tuile icone={entree.icone} nom={entree.nom} taille={dense ? 32 : 48} />
      <span className={cn("flex min-w-0 flex-1 flex-col", dense ? "gap-0" : "gap-1")}>
        <span className="flex min-w-0 items-center gap-2">
          <span className={cn("truncate font-semibold text-fg", dense ? "text-[13px] leading-tight" : "text-[15px]")}>
            {entree.nom}
          </span>
          {entree.exAequo && (
            <span className="cl-etiquette shrink-0 text-[9px] text-faint">ex æquo</span>
          )}
          {entree.estMoi && <span className="cl-toi">Toi</span>}
          {dense && entree.sousNom && (
            <span className="truncate text-[11px] text-faint">{entree.sousNom}</span>
          )}
        </span>
        {!dense && entree.sousNom && (
          <span className="truncate text-[11px] text-muted">{entree.sousNom}</span>
        )}
        {!dense && entree.plateformes && entree.plateformes.length > 0 && (
          <span className="flex gap-1">
            {entree.plateformes.map((p) => (
              <span key={p} className="cl-plateforme" title={PLATEFORMES[p].titre}>
                {PLATEFORMES[p].icone}
              </span>
            ))}
          </span>
        )}
        {/* Ligne serrée : le détail passe SOUS le nom. À gauche du montant (qui
            ne rétrécit pas), il écrasait le nom sur un téléphone. */}
        {dense && entree.detail && (
          <span className="truncate text-[11px] leading-tight text-muted">{entree.detail}</span>
        )}
      </span>
      <span className="flex shrink-0 flex-col items-end">
        <span className="flex items-baseline gap-0.5">
          <span
            className={cn(
              "cl-montant font-semibold",
              dense ? "text-[15px]" : "text-[20px]",
              cache ? "text-faint" : "text-fg",
            )}
          >
            {entree.montant === null ? "—" : formatAvec(entree.montant, { format, devise, formater })}
          </span>
          {!cache && suffixe && (
            <span className={cn("text-muted", dense ? "text-[11px]" : "text-[12px]")}>{suffixe}</span>
          )}
        </span>
        {!dense && entree.detail && <span className="text-[11px] text-muted">{entree.detail}</span>}
      </span>
    </>
  );

  return cliquable ? (
    <button
      type="button"
      className={classes}
      data-variante={variante}
      data-cliquable="true"
      data-moi={entree.estMoi ? "true" : "false"}
      onClick={() => onClick?.(entree)}
    >
      {contenu}
    </button>
  ) : (
    <div
      className={classes}
      data-variante={variante}
      data-cliquable="false"
      data-moi={entree.estMoi ? "true" : "false"}
    >
      {contenu}
    </div>
  );
}

export interface RangListeProps extends OptionsFormat {
  entrees: EntreeClassement[];
  /** Une petite étiquette en tête de la carte (« Top 5 · vues »), et son complément en gris — comme le `titre` de
   *  `ClassementPays` : un top dans sa carte, sans double cadre. */
  titre?: string;
  sousTitre?: string;
  onClick?: (entree: EntreeClassement) => void;
  dense?: boolean;
  /** Texte quand la liste est vide. */
  vide?: string;
  className?: string;
}

/** Les rangs 4 et plus, empilés dans une carte en verre, une hairline entre
 *  deux ; chaque ligne arrive en cascade. Une PAGE de lignes : la pagination
 *  reste chez l'appelant. */
export function RangListe({ entrees, titre, sousTitre, vide = "Personne d'autre pour l'instant.", className, ...reste }: RangListeProps) {
  const reduit = useReducedMotion() ?? false;
  return (
    <div className={cn("cl-root cl-carte divide-y divide-line", className)} role="list" aria-label={titre}>
      {titre && (
        <div className="flex min-w-0 items-baseline gap-2 whitespace-nowrap px-4 pb-1 pt-3">
          <span className="cl-etiquette shrink-0 text-[10px] text-faint">{titre}</span>
          {sousTitre && <span className="cl-etiquette min-w-0 truncate text-[9px] normal-case tracking-normal text-faint">{sousTitre}</span>}
        </div>
      )}
      {entrees.length === 0 && (
        <div className="px-4 py-6 text-center text-[12px] text-muted">{vide}</div>
      )}
      {entrees.map((entree, i) => (
        <motion.div
          key={cleDe(entree)}
          role="listitem"
          initial={reduit ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.06 * i, duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
        >
          <RangCarte entree={entree} variante="ligne" {...reste} />
        </motion.div>
      ))}
    </div>
  );
}
